import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Normalizes any URL string by handling 'www.', 'http://', 'https://', and protocol-less domains.
 * Ensures the output has a valid web protocol (defaulting to https://) and handles trimming.
 */
export function normalizeUrl(url: string | undefined | null, defaultProtocol: string = 'https://'): string {
  if (!url) return '';
  const trimmed = url.trim();
  if (!trimmed) return '';

  // Return unchanged if already using standard schemes or data URIs
  if (
    trimmed.startsWith('https://') ||
    trimmed.startsWith('http://') ||
    trimmed.startsWith('mailto:') ||
    trimmed.startsWith('tel:') ||
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:')
  ) {
    return trimmed;
  }

  // Protocol-relative URLs (e.g. //example.com/foo)
  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`;
  }

  // If starts with www. or domain format, prepend https://
  return `${defaultProtocol}${trimmed}`;
}

/**
 * Identifies the video platform (Instagram, YouTube, TikTok, or Genérico)
 * regardless of whether the URL includes https://, http://, www., or just the domain.
 */
export function detectVideoSource(url: string): string {
  if (!url || !url.trim()) return 'Vacío';
  const u = url.toLowerCase().trim();
  if (u.includes('instagram.com') || u.includes('instagr.am')) return 'Instagram';
  if (u.includes('youtube.com') || u.includes('youtu.be')) return 'YouTube';
  if (u.includes('tiktok.com')) return 'TikTok';
  return 'Genérico';
}

/**
 * Transforms video links (Instagram Reels, YouTube Shorts/Watch, TikTok, or generic embeds)
 * into embeddable iframe URLs. Accurately parses URLs with 'www.', 'https://', 'http://',
 * or without any protocol (e.g., 'www.instagram.com/reel/...', 'youtube.com/shorts/...').
 */
export function getEmbedUrl(rawUrl: string): string | null {
  if (!rawUrl) return null;
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  // Instagram: handles www.instagram.com, instagram.com, instagr.am with /reel/, /p/, /tv/
  // with or without https/http
  const instagramRegex = /(?:https?:\/\/)?(?:www\.)?(?:instagram\.com|instagr\.am)\/(?:reel|p|tv)\/([A-Za-z0-9_-]+)/i;
  const instMatch = trimmed.match(instagramRegex);
  if (instMatch && instMatch[1]) {
    return `https://www.instagram.com/reel/${instMatch[1]}/embed/`;
  }

  // YouTube Shorts, standard watch, youtu.be, or embed
  const ytShortsRegex = /(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com\/(?:shorts\/|watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]+)/i;
  const ytMatch = trimmed.match(ytShortsRegex);
  if (ytMatch && ytMatch[1]) {
    return `https://www.youtube.com/embed/${ytMatch[1]}`;
  }

  // TikTok: handles @user/video/ID, /v/ID, /t/ID, /video/ID, embed/v2/ID
  const tiktokRegex = /(?:https?:\/\/)?(?:www\.|m\.|vm\.|vt\.)?tiktok\.com\/(?:@[^\/]+\/video\/|v\/|t\/|video\/|embed\/v2\/)?(\d+)/i;
  const tiktokMatch = trimmed.match(tiktokRegex);
  if (tiktokMatch && tiktokMatch[1]) {
    return `https://www.tiktok.com/embed/v2/${tiktokMatch[1]}`;
  }

  // If already an embed URL (e.g. iframe link with or without https)
  if (trimmed.includes('/embed/') || trimmed.includes('/embed')) {
    return normalizeUrl(trimmed);
  }

  // If starts with http://, https://, or www. / domain, normalize to https://
  if (
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.startsWith('www.') ||
    /^[a-zA-Z0-9-]+\.[a-zA-Z]{2,}/.test(trimmed)
  ) {
    return normalizeUrl(trimmed);
  }

  return null;
}

